import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { transfersApi, communicationApi, transfersV2Api } from '../services/api';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Plus, 
  Search, 
  MoreVertical,
  CheckCircle2,
  XCircle,
  Clock,
  Eye,
  Mail,
  MessageSquare,
  RefreshCw,
  Loader2,
  Filter,
  Download,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const CURRENCIES = ['EUR', 'USD', 'CHF', 'XPF', 'GBP'];
const LANGUAGES = [
  { code: 'fr', name: 'Français' },
  { code: 'en', name: 'Anglais' },
  { code: 'es', name: 'Espagnol' },
  { code: 'pt', name: 'Portugais' },
  { code: 'it', name: 'Italien' },
];
const STATUSES = [
  { value: 'pending', label: 'En attente' },
  { value: 'executed', label: 'Exécuté' },
  { value: 'rejected', label: 'Rejeté' },
];

const TransfersPage = ({ apiOverride, titleOverride, subtitleOverride, testIdPrefix = '' }) => {
  const api = apiOverride || transfersApi;
  const { admin } = useAuth();
  const isAdmin = admin?.role === 'admin';
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('');
  const [viewFilter, setViewFilter] = useState(isAdmin ? 'mine' : '');
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showSmsModal, setShowSmsModal] = useState(false);
  
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [senderBanks, setSenderBanks] = useState([]);

  // Form states
  const [createForm, setCreateForm] = useState({
    beneficiary_name: '',
    iban: '',
    beneficiary_bank: '',
    sender_bank: '',
    client_email: '',
    payment_reason: '',
    currency: 'EUR',
    client_language: 'fr',
    amount: '',
    transfer_fees: '0',
  });

  const [rejectForm, setRejectForm] = useState({
    reason: '',
    additional_fees: '0',
  });

  const [emailForm, setEmailForm] = useState({
    recipient_email: '',
    subject: '',
    message: '',
  });

  const [smsForm, setSmsForm] = useState({
    phone_number: '',
    message: '',
  });

  const fetchTransfers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (currencyFilter) params.currency = currencyFilter;
      if (viewFilter) params.view = viewFilter;
      
      const response = await api.getAll(params);
      setTransfers(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des virements');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, currencyFilter, viewFilter, api]);

  useEffect(() => {
    fetchTransfers();
  }, [fetchTransfers]);

  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/sender-banks`);
        setSenderBanks(response.data);
      } catch (error) {
        console.error('Error fetching sender banks:', error);
      }
    };
    fetchBanks();
  }, []);

  const handleCreateTransfer = async (e) => {
    if (e) e.preventDefault();
    // First show confirmation modal
    if (!showConfirmModal) {
      if (!createForm.beneficiary_name || !createForm.amount || !createForm.client_email || !createForm.sender_bank) {
        toast.error('Veuillez remplir tous les champs obligatoires');
        return;
      }
      setShowCreateModal(false);
      setTimeout(() => setShowConfirmModal(true), 200);
      return;
    }
    // Actual creation after confirmation
    setFormLoading(true);
    try {
      await api.create({
        ...createForm,
        amount: parseFloat(createForm.amount),
        transfer_fees: parseFloat(createForm.transfer_fees),
      });
      toast.success('Virement cree avec succes');
      setShowConfirmModal(false);
      setCreateForm({
        beneficiary_name: '',
        iban: '',
        beneficiary_bank: '',
        sender_bank: '',
        client_email: '',
        payment_reason: '',
        currency: 'EUR',
        client_language: 'fr',
        amount: '',
        transfer_fees: '0',
      });
      fetchTransfers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la creation');
    } finally {
      setFormLoading(false);
    }
  };

  const handleConfirmBack = () => {
    setShowConfirmModal(false);
    setShowCreateModal(true);
  };

  const handleExecute = async (transfer) => {
    try {
      await api.execute(transfer.id);
      toast.success('Virement exécuté avec succès');
      fetchTransfers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'exécution');
    }
  };

  const handleSetPending = async (transfer) => {
    try {
      await api.setPending(transfer.id);
      toast.success('Virement mis en attente');
      fetchTransfers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleReject = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await api.reject(selectedTransfer.id, {
        reason: rejectForm.reason,
        additional_fees: parseFloat(rejectForm.additional_fees),
      });
      toast.success('Virement rejeté');
      setShowRejectModal(false);
      setRejectForm({ reason: '', additional_fees: '0' });
      fetchTransfers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du rejet');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (transfer) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce virement ?')) return;
    try {
      await api.delete(transfer.id);
      toast.success('Virement supprimé');
      fetchTransfers();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await communicationApi.sendEmail(emailForm);
      toast.success('Email envoyé avec succès');
      setShowEmailModal(false);
      setEmailForm({ recipient_email: '', subject: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setFormLoading(false);
    }
  };

  const handleSendSms = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await communicationApi.sendSMS(smsForm);
      toast.success('SMS envoyé (MOCK)');
      setShowSmsModal(false);
      setSmsForm({ phone_number: '', message: '' });
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setFormLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-50 text-amber-700 border-amber-200',
      executed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      rejected: 'bg-red-50 text-red-700 border-red-200',
    };
    const labels = {
      pending: 'En attente',
      executed: 'Exécuté',
      rejected: 'Rejeté',
    };
    return (
      <Badge variant="outline" className={`${styles[status]} rounded-full text-xs`}>
        {labels[status]}
      </Badge>
    );
  };

  const formatAmount = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency,
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const exportToPDF = () => {
    // Client-side PDF export
    const printContent = document.getElementById('transfers-table');
    const printWindow = window.open('', '', 'width=800,height=600');
    printWindow.document.write('<html><head><title>Export Virements</title>');
    printWindow.document.write('<style>table{width:100%;border-collapse:collapse;}th,td{border:1px solid #ccc;padding:8px;text-align:left;}th{background:#f5f5f5;}</style>');
    printWindow.document.write('</head><body>');
    printWindow.document.write('<h1>Liste des Virements - WorldPay</h1>');
    printWindow.document.write('<p>Date d\'export: ' + new Date().toLocaleString('fr-FR') + '</p>');
    printWindow.document.write(printContent?.innerHTML || '');
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };

  const handleDownloadPDF = async (transfer) => {
    try {
      const response = await api.downloadPdf(transfer.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      const ref = transfer.id.slice(0, 8).toUpperCase();
      a.download = `WorldPay_Virement_${ref}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Erreur lors du téléchargement du PDF');
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="transfers-page">
        {/* Header */}
        <div className="flex flex-col gap-4">
          <div>
            <h1 className="font-heading font-bold text-xl md:text-2xl text-slate-900">{titleOverride || 'Gestion des Virements'}</h1>
            <p className="text-slate-500 text-sm mt-1">{subtitleOverride || 'Creez, gerez et suivez les virements clients'}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button 
              variant="outline" 
              className="rounded-lg flex-1 sm:flex-none"
              onClick={() => setShowEmailModal(true)}
              data-testid="send-email-btn"
            >
              <Mail className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Email</span>
            </Button>
            <Button 
              variant="outline" 
              className="rounded-lg flex-1 sm:flex-none"
              onClick={() => setShowSmsModal(true)}
              data-testid="send-sms-btn"
            >
              <MessageSquare className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">SMS</span>
            </Button>
            <Button 
              className="bg-[#0066FF] hover:bg-[#0052CC] rounded-lg flex-1 sm:flex-none"
              onClick={() => setShowCreateModal(true)}
              data-testid="create-transfer-btn"
            >
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Nouveau Virement</span>
              <span className="sm:hidden">Nouveau</span>
            </Button>
          </div>
        </div>

        {/* View Tabs (admin only) */}
        {isAdmin && (
          <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit" data-testid="view-tabs">
            <Button
              variant={viewFilter === 'mine' ? 'default' : 'ghost'}
              size="sm"
              className={`rounded-lg text-xs ${viewFilter === 'mine' ? 'bg-[#0B1426] hover:bg-[#0B1426]/90' : ''}`}
              onClick={() => setViewFilter('mine')}
              data-testid="view-tab-mine"
            >
              Mes virements
            </Button>
            <Button
              variant={viewFilter === 'users' ? 'default' : 'ghost'}
              size="sm"
              className={`rounded-lg text-xs ${viewFilter === 'users' ? 'bg-[#0B1426] hover:bg-[#0B1426]/90' : ''}`}
              onClick={() => setViewFilter('users')}
              data-testid="view-tab-users"
            >
              Virements utilisateurs
            </Button>
            <Button
              variant={viewFilter === '' ? 'default' : 'ghost'}
              size="sm"
              className={`rounded-lg text-xs ${viewFilter === '' ? 'bg-[#0B1426] hover:bg-[#0B1426]/90' : ''}`}
              onClick={() => setViewFilter('')}
              data-testid="view-tab-all"
            >
              Tous
            </Button>
          </div>
        )}

        {/* Filters */}
        <Card className="border-slate-200 shadow-sm rounded-lg">
          <CardContent className="p-3 md:p-4">
            <div className="flex flex-col gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 rounded-lg"
                  data-testid="search-input"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-32 rounded-lg" data-testid="status-filter">
                    <Filter className="w-4 h-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="Statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    {STATUSES.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={currencyFilter} onValueChange={setCurrencyFilter}>
                  <SelectTrigger className="w-full sm:w-28 rounded-lg" data-testid="currency-filter">
                    <SelectValue placeholder="Devise" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes</SelectItem>
                    {CURRENCIES.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex gap-2 w-full sm:w-auto">
                  <Button variant="outline" className="rounded-lg flex-1 sm:flex-none" onClick={exportToPDF} data-testid="export-btn">
                    <Download className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Export</span>
                  </Button>
                  <Button variant="outline" className="rounded-lg flex-1 sm:flex-none" onClick={fetchTransfers} data-testid="refresh-btn">
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Mobile Cards View */}
        <div className="block md:hidden space-y-3">
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto text-[#0066FF]" />
            </div>
          ) : transfers.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="text-center py-12 text-slate-400">
                Aucun virement trouvé
              </CardContent>
            </Card>
          ) : (
            transfers.map((transfer) => (
              <Card key={transfer.id} className="border-slate-200 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-medium text-slate-900">{transfer.beneficiary_name}</p>
                      <p className="text-xs text-slate-500">{transfer.beneficiary_bank}</p>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onClick={() => { setSelectedTransfer(transfer); setShowDetailModal(true); }}>
                          <Eye className="w-4 h-4 mr-2" />
                          Voir détails
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDownloadPDF(transfer)}>
                          <Download className="w-4 h-4 mr-2" />
                          Télécharger PDF
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {transfer.status !== 'executed' && (
                          <DropdownMenuItem onClick={() => handleExecute(transfer)} className="text-emerald-600">
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Exécuter
                          </DropdownMenuItem>
                        )}
                        {transfer.status !== 'pending' && (
                          <DropdownMenuItem onClick={() => handleSetPending(transfer)} className="text-amber-600">
                            <Clock className="w-4 h-4 mr-2" />
                            Mettre en attente
                          </DropdownMenuItem>
                        )}
                        {transfer.status !== 'rejected' && (
                          <DropdownMenuItem 
                            onClick={() => { setSelectedTransfer(transfer); setShowRejectModal(true); }}
                            className="text-red-600"
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Rejeter
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleDelete(transfer)} className="text-red-600">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Supprimer
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">IBAN</span>
                      <span className="font-mono text-xs truncate max-w-[150px]">{transfer.iban}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Email</span>
                      <span className="text-xs truncate max-w-[150px]">{transfer.client_email}</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-slate-100">
                      <span className="font-heading font-bold text-lg">{formatAmount(transfer.amount, transfer.currency)}</span>
                      {getStatusBadge(transfer.status)}
                    </div>
                    <p className="text-xs text-slate-400">{formatDate(transfer.created_at)}</p>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Desktop Table */}
        <Card className="border-slate-200 shadow-sm rounded-lg overflow-hidden hidden md:block">
          <div className="overflow-x-auto" id="transfers-table">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Bénéficiaire</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">IBAN</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Email</th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Montant</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Statut</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Date</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto text-[#0066FF]" />
                    </td>
                  </tr>
                ) : transfers.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-slate-400">
                      Aucun virement trouvé
                    </td>
                  </tr>
                ) : (
                  transfers.map((transfer, index) => (
                    <tr 
                      key={transfer.id} 
                      className="border-b border-slate-100 hover:bg-slate-50/80 transition-colors animate-fade-in"
                      style={{ animationDelay: `${index * 0.03}s` }}
                    >
                      <td className="py-3 px-4">
                        <p className="font-medium text-sm text-slate-900">{transfer.beneficiary_name}</p>
                        <p className="text-xs text-slate-500">{transfer.beneficiary_bank}</p>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600 font-mono">{transfer.iban}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{transfer.client_email}</td>
                      <td className="py-3 px-4 text-right font-heading font-bold text-slate-900">
                        {formatAmount(transfer.amount, transfer.currency)}
                      </td>
                      <td className="py-3 px-4 text-center">{getStatusBadge(transfer.status)}</td>
                      <td className="py-3 px-4 text-sm text-slate-500">{formatDate(transfer.created_at)}</td>
                      <td className="py-3 px-4 text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`transfer-actions-${transfer.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem onClick={() => { setSelectedTransfer(transfer); setShowDetailModal(true); }}>
                              <Eye className="w-4 h-4 mr-2" />
                              Voir détails
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDownloadPDF(transfer)}>
                              <Download className="w-4 h-4 mr-2" />
                              Télécharger PDF
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {transfer.status !== 'executed' && (
                              <DropdownMenuItem onClick={() => handleExecute(transfer)} className="text-emerald-600">
                                <CheckCircle2 className="w-4 h-4 mr-2" />
                                Exécuter
                              </DropdownMenuItem>
                            )}
                            {transfer.status !== 'pending' && (
                              <DropdownMenuItem onClick={() => handleSetPending(transfer)} className="text-amber-600">
                                <Clock className="w-4 h-4 mr-2" />
                                Mettre en attente
                              </DropdownMenuItem>
                            )}
                            {transfer.status !== 'rejected' && (
                              <DropdownMenuItem 
                                onClick={() => { setSelectedTransfer(transfer); setShowRejectModal(true); }}
                                className="text-red-600"
                              >
                                <XCircle className="w-4 h-4 mr-2" />
                                Rejeter
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => { 
                                setEmailForm({ recipient_email: transfer.client_email, subject: '', message: '' });
                                setShowEmailModal(true);
                              }}
                            >
                              <Mail className="w-4 h-4 mr-2" />
                              Envoyer email
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleDelete(transfer)} className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Create Transfer Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto mx-4 sm:mx-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Nouveau Virement</DialogTitle>
              <DialogDescription>Créez un nouveau virement bancaire</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateTransfer} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="beneficiary_name">Nom du bénéficiaire *</Label>
                  <Input id="beneficiary_name" value={createForm.beneficiary_name} onChange={(e) => setCreateForm({...createForm, beneficiary_name: e.target.value})} required className="rounded-lg" data-testid="beneficiary-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="iban">IBAN / RIB *</Label>
                  <Input id="iban" value={createForm.iban} onChange={(e) => setCreateForm({...createForm, iban: e.target.value.toUpperCase()})} required className="rounded-lg font-mono" placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX" data-testid="iban-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="beneficiary_bank">Banque bénéficiaire *</Label>
                  <Input id="beneficiary_bank" value={createForm.beneficiary_bank} onChange={(e) => setCreateForm({...createForm, beneficiary_bank: e.target.value})} required className="rounded-lg" data-testid="beneficiary-bank-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sender_bank">Banque émettrice *</Label>
                  <Select value={createForm.sender_bank} onValueChange={(value) => setCreateForm({...createForm, sender_bank: value})} required>
                    <SelectTrigger className="rounded-lg" data-testid="sender-bank-select"><SelectValue placeholder="Sélectionner une banque" /></SelectTrigger>
                    <SelectContent>
                      {senderBanks.map((bank) => (
                        <SelectItem key={bank.name} value={bank.name} data-testid={`sender-bank-option-${bank.name}`}>
                          <div className="flex items-center gap-2">
                            <img src={bank.logo_url} alt={bank.name} className="h-5 w-auto max-w-[80px] object-contain" />
                            <span>{bank.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client_email">Email du client *</Label>
                  <Input id="client_email" type="email" value={createForm.client_email} onChange={(e) => setCreateForm({...createForm, client_email: e.target.value})} required className="rounded-lg" data-testid="client-email-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client_language">Langue du client</Label>
                  <Select value={createForm.client_language} onValueChange={(v) => setCreateForm({...createForm, client_language: v})}>
                    <SelectTrigger className="rounded-lg" data-testid="language-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{LANGUAGES.map(l => <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="amount">Montant *</Label>
                  <Input id="amount" type="number" step="0.01" min="0" value={createForm.amount} onChange={(e) => setCreateForm({...createForm, amount: e.target.value})} required className="rounded-lg" data-testid="amount-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="currency">Devise</Label>
                  <Select value={createForm.currency} onValueChange={(v) => setCreateForm({...createForm, currency: v})}>
                    <SelectTrigger className="rounded-lg" data-testid="currency-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="transfer_fees">Frais de transfert</Label>
                  <Input id="transfer_fees" type="number" step="0.01" min="0" value={createForm.transfer_fees} onChange={(e) => setCreateForm({...createForm, transfer_fees: e.target.value})} className="rounded-lg" data-testid="fees-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="payment_reason">Motif du paiement *</Label>
                <Textarea id="payment_reason" value={createForm.payment_reason} onChange={(e) => setCreateForm({...createForm, payment_reason: e.target.value})} required className="rounded-lg" rows={3} data-testid="payment-reason-input" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)} className="rounded-lg">Annuler</Button>
                <Button type="button" className="bg-[#0066FF] hover:bg-[#0052CC] rounded-lg" disabled={formLoading} data-testid="submit-transfer-btn" onClick={handleCreateTransfer}>
                  {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Creer le virement
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Transfer Detail Modal */}
        <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">Recapitulatif du virement</DialogTitle>
            </DialogHeader>
            <div className="space-y-4" data-testid="confirm-modal">
              <p className="text-sm text-slate-500">Veuillez verifier les informations avant de valider :</p>
              <div className="bg-slate-50 rounded-lg p-5 space-y-3 border border-slate-100">
                <div className="flex justify-between items-center pb-3 border-b border-slate-200">
                  <span className="text-sm text-slate-500">Beneficiaire</span>
                  <span className="text-sm font-semibold text-slate-900">{createForm.beneficiary_name}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-slate-200">
                  <span className="text-sm text-slate-500">Email</span>
                  <span className="text-sm font-medium text-slate-700">{createForm.client_email}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-slate-200">
                  <span className="text-sm text-slate-500">IBAN</span>
                  <span className="text-sm font-mono text-slate-700">{createForm.iban || '-'}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-slate-200">
                  <span className="text-sm text-slate-500">Banque emettrice</span>
                  <span className="text-sm font-medium text-slate-700">{createForm.sender_bank}</span>
                </div>
                {createForm.beneficiary_bank && (
                  <div className="flex justify-between items-center pb-3 border-b border-slate-200">
                    <span className="text-sm text-slate-500">Banque beneficiaire</span>
                    <span className="text-sm font-medium text-slate-700">{createForm.beneficiary_bank}</span>
                  </div>
                )}
                {createForm.payment_reason && (
                  <div className="flex justify-between items-center pb-3 border-b border-slate-200">
                    <span className="text-sm text-slate-500">Motif</span>
                    <span className="text-sm font-medium text-slate-700">{createForm.payment_reason}</span>
                  </div>
                )}
                <div className="flex justify-between items-center pb-3 border-b border-slate-200">
                  <span className="text-sm text-slate-500">Devise / Langue</span>
                  <span className="text-sm font-medium text-slate-700">{createForm.currency} / {createForm.client_language?.toUpperCase()}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-slate-200">
                  <span className="text-sm text-slate-500">Frais</span>
                  <span className="text-sm font-medium text-slate-700">{createForm.transfer_fees || '0'} {createForm.currency}</span>
                </div>
                <div className="flex justify-between items-center pt-1">
                  <span className="text-base font-semibold text-slate-900">Montant</span>
                  <span className="text-xl font-bold text-[#0066FF]">{parseFloat(createForm.amount || 0).toLocaleString('fr-FR', {minimumFractionDigits: 2})} {createForm.currency}</span>
                </div>
              </div>
            </div>
            <DialogFooter className="flex gap-2 sm:gap-2">
              <Button type="button" variant="outline" onClick={handleConfirmBack} className="rounded-lg" data-testid="confirm-back-btn">
                Modifier
              </Button>
              <Button onClick={() => { handleCreateTransfer(); }} className="bg-[#0066FF] hover:bg-[#0052CC] rounded-lg" disabled={formLoading} data-testid="confirm-validate-btn">
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Valider le virement
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Transfer Detail Modal (view) */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-heading">Détails du Virement</DialogTitle></DialogHeader>
            {selectedTransfer && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-xs text-slate-500 uppercase tracking-wider">Bénéficiaire</p><p className="font-medium">{selectedTransfer.beneficiary_name}</p></div>
                  <div><p className="text-xs text-slate-500 uppercase tracking-wider">Statut</p>{getStatusBadge(selectedTransfer.status)}</div>
                  <div><p className="text-xs text-slate-500 uppercase tracking-wider">IBAN</p><p className="font-mono text-sm">{selectedTransfer.iban}</p></div>
                  <div><p className="text-xs text-slate-500 uppercase tracking-wider">Montant</p><p className="font-heading font-bold">{formatAmount(selectedTransfer.amount, selectedTransfer.currency)}</p></div>
                  <div><p className="text-xs text-slate-500 uppercase tracking-wider">Banque bénéficiaire</p><p>{selectedTransfer.beneficiary_bank}</p></div>
                  <div><p className="text-xs text-slate-500 uppercase tracking-wider">Banque émettrice</p><p>{selectedTransfer.sender_bank}</p></div>
                  <div><p className="text-xs text-slate-500 uppercase tracking-wider">Email client</p><p className="text-sm">{selectedTransfer.client_email}</p></div>
                  <div><p className="text-xs text-slate-500 uppercase tracking-wider">Frais</p><p>{formatAmount(selectedTransfer.transfer_fees, selectedTransfer.currency)}</p></div>
                  <div className="col-span-2"><p className="text-xs text-slate-500 uppercase tracking-wider">Motif</p><p>{selectedTransfer.payment_reason}</p></div>
                  {selectedTransfer.rejection_reason && (
                    <div className="col-span-2 p-3 bg-red-50 rounded-lg border border-red-200">
                      <p className="text-xs text-red-600 uppercase tracking-wider">Motif du rejet</p>
                      <p className="text-red-700">{selectedTransfer.rejection_reason}</p>
                    </div>
                  )}
                  <div><p className="text-xs text-slate-500 uppercase tracking-wider">Créé le</p><p className="text-sm">{formatDate(selectedTransfer.created_at)}</p></div>
                  {selectedTransfer.executed_at && <div><p className="text-xs text-slate-500 uppercase tracking-wider">Exécuté le</p><p className="text-sm">{formatDate(selectedTransfer.executed_at)}</p></div>}
                </div>
                <Button data-testid="detail-download-pdf" variant="outline" className="w-full mt-2" onClick={() => handleDownloadPDF(selectedTransfer)}>
                  <Download className="w-4 h-4 mr-2" />Télécharger l'attestation PDF
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Reject Modal */}
        <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-red-600">Rejeter le Virement</DialogTitle>
              <DialogDescription>Indiquez le motif du rejet et les frais éventuels</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleReject} className="space-y-4">
              <div className="space-y-2"><Label htmlFor="reject_reason">Motif du rejet *</Label><Textarea id="reject_reason" value={rejectForm.reason} onChange={(e) => setRejectForm({...rejectForm, reason: e.target.value})} required className="rounded-lg" rows={3} data-testid="reject-reason-input" /></div>
              <div className="space-y-2"><Label htmlFor="additional_fees">Frais additionnels</Label><Input id="additional_fees" type="number" step="0.01" min="0" value={rejectForm.additional_fees} onChange={(e) => setRejectForm({...rejectForm, additional_fees: e.target.value})} className="rounded-lg" data-testid="additional-fees-input" /></div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowRejectModal(false)} className="rounded-lg">Annuler</Button>
                <Button type="submit" variant="destructive" className="rounded-lg" disabled={formLoading} data-testid="confirm-reject-btn">{formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Rejeter</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Email Modal */}
        <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">Envoyer un Email</DialogTitle><DialogDescription>Envoyez un email personnalisé au client</DialogDescription></DialogHeader>
            <form onSubmit={handleSendEmail} className="space-y-4">
              <div className="space-y-2"><Label htmlFor="email_recipient">Email destinataire *</Label><Input id="email_recipient" type="email" value={emailForm.recipient_email} onChange={(e) => setEmailForm({...emailForm, recipient_email: e.target.value})} required className="rounded-lg" data-testid="email-recipient-input" /></div>
              <div className="space-y-2"><Label htmlFor="email_subject">Sujet *</Label><Input id="email_subject" value={emailForm.subject} onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})} required className="rounded-lg" data-testid="email-subject-input" /></div>
              <div className="space-y-2"><Label htmlFor="email_message">Message *</Label><Textarea id="email_message" value={emailForm.message} onChange={(e) => setEmailForm({...emailForm, message: e.target.value})} required className="rounded-lg" rows={5} data-testid="email-message-input" /></div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowEmailModal(false)} className="rounded-lg">Annuler</Button>
                <Button type="submit" className="bg-[#0B1426] hover:bg-[#0B1426]/90 rounded-lg" disabled={formLoading} data-testid="send-email-submit-btn">{formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Envoyer</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* SMS Modal */}
        <Dialog open={showSmsModal} onOpenChange={setShowSmsModal}>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">Envoyer un SMS</DialogTitle><DialogDescription>Envoyez un SMS au client (MOCK)</DialogDescription></DialogHeader>
            <form onSubmit={handleSendSms} className="space-y-4">
              <div className="space-y-2"><Label htmlFor="sms_phone">Numéro de téléphone *</Label><Input id="sms_phone" value={smsForm.phone_number} onChange={(e) => setSmsForm({...smsForm, phone_number: e.target.value})} required className="rounded-lg" placeholder="+33 6 XX XX XX XX" data-testid="sms-phone-input" /></div>
              <div className="space-y-2"><Label htmlFor="sms_message">Message *</Label><Textarea id="sms_message" value={smsForm.message} onChange={(e) => setSmsForm({...smsForm, message: e.target.value})} required className="rounded-lg" rows={3} maxLength={160} data-testid="sms-message-input" /><p className="text-xs text-slate-500">{smsForm.message.length}/160 caractères</p></div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowSmsModal(false)} className="rounded-lg">Annuler</Button>
                <Button type="submit" className="bg-[#0B1426] hover:bg-[#0B1426]/90 rounded-lg" disabled={formLoading} data-testid="send-sms-submit-btn">{formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Envoyer</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export { TransfersPage };
export default TransfersPage;
